"use client";

import { useLocalePathname } from "@i18n/routing";
import { useMarketingHomeScrollPending } from "@marketing/shared/components/marketing-home-scroll-pending-context";
import {
	computeActiveMarketingNavSectionId,
	marketingSiteHeaderBottomPx,
	parseMarketingLandingHash,
	replaceMarketingLandingHash,
} from "@marketing/shared/lib/marketing-landing-home-sections";
import { useEffect, useRef, useState } from "react";

/**
 * Tracks which marketing landing section aligns with the sticky header while scrolling,
 * drives nav highlighting, and mirrors viewport state into the URL hash via
 * {@link replaceMarketingLandingHash} (no extra history entries).
 *
 * While a hash-nav pending section is set, viewport spy updates are frozen so the active
 * nav label does not flicker mid smooth-scroll; pending clears when spy matches or on
 * `scrollend`.
 */
export function useMarketingHomeScrollSpy(): string | null {
	const pathname = useLocalePathname();
	const enabled = pathname === "/";
	const { pendingLandingSectionId, setPendingLandingSectionId } =
		useMarketingHomeScrollPending();

	const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
	const lastSyncedHashRef = useRef<string | null>(null);
	const pendingRef = useRef(pendingLandingSectionId);
	pendingRef.current = pendingLandingSectionId;

	useEffect(() => {
		if (!enabled) {
			lastSyncedHashRef.current = null;
			setActiveSectionId(null);
			return;
		}

		let raf = 0;

		const indicatorFromViewport = () =>
			computeActiveMarketingNavSectionId(marketingSiteHeaderBottomPx());

		const syncHashIfNeeded = (next: string | null) => {
			if (pendingRef.current != null) {
				return;
			}
			if (lastSyncedHashRef.current === next) {
				return;
			}
			lastSyncedHashRef.current = next;
			replaceMarketingLandingHash(next);
		};

		const tickViewport = () => {
			const next = indicatorFromViewport();
			const pending = pendingRef.current;
			if (pending != null) {
				if (next === pending) {
					setPendingLandingSectionId(null);
					setActiveSectionId(next);
					lastSyncedHashRef.current = next;
					replaceMarketingLandingHash(next);
				}
				// Freeze spy-driven UI/hash while pending — prevents mid-smooth flicker.
				return;
			}
			setActiveSectionId((prev) => (prev === next ? prev : next));
			syncHashIfNeeded(next);
		};

		const scheduleViewportTick = () => {
			cancelAnimationFrame(raf);
			raf = requestAnimationFrame(tickViewport);
		};

		const onScrollEnd = () => {
			cancelAnimationFrame(raf);
			const pending = pendingRef.current;
			if (pending != null) {
				// Commit pending highlight even if spy is briefly lagging, then re-measure.
				setActiveSectionId(pending);
				lastSyncedHashRef.current = pending;
				replaceMarketingLandingHash(pending);
				setPendingLandingSectionId(null);
			}
			tickViewport();
		};

		const onPopState = () => {
			setPendingLandingSectionId(null);
			const raw = parseMarketingLandingHash();
			if (raw != null) {
				lastSyncedHashRef.current = raw;
				setActiveSectionId(raw);
				return;
			}
			tickViewport();
		};

		const hashBoot = parseMarketingLandingHash();
		if (hashBoot != null) {
			lastSyncedHashRef.current = hashBoot;
			setActiveSectionId(hashBoot);
			requestAnimationFrame(tickViewport);
		} else {
			tickViewport();
		}

		window.addEventListener("scroll", scheduleViewportTick, {
			passive: true,
		});
		window.addEventListener("resize", scheduleViewportTick);
		window.addEventListener("popstate", onPopState);
		window.addEventListener("scrollend", onScrollEnd);

		return () => {
			cancelAnimationFrame(raf);
			window.removeEventListener("scroll", scheduleViewportTick);
			window.removeEventListener("resize", scheduleViewportTick);
			window.removeEventListener("popstate", onPopState);
			window.removeEventListener("scrollend", onScrollEnd);
		};
	}, [enabled, setPendingLandingSectionId]);

	return enabled ? activeSectionId : null;
}
